import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { hebrewDateString } from '@/lib/hebcal';
import { fetchBillingEnabled } from '@/services/subscription';

const HebrewDate = () => {
  const [dateStr, setDateStr] = useState('');
  useEffect(() => {
    setDateStr(hebrewDateString(new Date()));
  }, []);
  return (
    <span className="font-hebrew text-[13px] text-gold">
      {dateStr}
    </span>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [billingEnabled, setBillingEnabled] = useState(false);

  useEffect(() => {
    fetchBillingEnabled().then(setBillingEnabled);
  }, []);

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-midnight">
        <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  const featurePills = [
    { icon: 'ri-building-2-line', label: 'Hebrew Calendar' },
    { icon: 'ri-phone-line', label: 'Auto Call Reminders' },
    { icon: 'ri-fire-line', label: 'Streak Tracking' },
    { icon: 'ri-star-line', label: 'Shabbat Aware' },
  ];

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(to bottom, #0F172A 0%, #0A0A0F 100%)' }}
    >
      {/* Subtle star-field / dot pattern with slow parallax drift */}
      <div
        className="absolute inset-0 opacity-[0.10] pointer-events-none starfield-drift"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      {/* Gold ambient glow */}
      <div className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center text-center px-6 w-full max-w-sm py-12">
        {/* CodeLab identity badge */}
        <div className="mb-8 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-electric/10 border border-electric/30">
          <i className="ri-terminal-box-line text-electric text-sm" />
          <span className="text-[11px] font-medium text-electric">by CodeLab</span>
        </div>

        {/* Icon */}
        <img
          src="/icon-192.png"
          alt="Zman logo"
          className="w-20 h-20 rounded-2xl mb-6 animate-fade-up"
          style={{ boxShadow: '0 0 40px rgba(212,168,67,0.4)' }}
        />

        {/* Title */}
        <h1 className="font-display text-5xl font-bold text-white mb-2 tracking-tight">
          Zman
        </h1>

        {/* Tagline */}
        <p className="text-lg font-medium text-gold mb-5">Your Time. Your Habits.</p>

        {/* Description */}
        <p className="text-sm text-white/60 leading-[1.7] mb-6 max-w-[280px]">
          Build consistent daily habits with automated reminders, streak tracking, and full Hebrew calendar support.
        </p>

        {/* Hebrew date */}
        <div className="mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gold/40 bg-gold/[0.08] text-gold text-xs font-medium">
            <i className="ri-calendar-line" />
            <HebrewDate />
          </span>
        </div>

        {/* Feature pills */}
        <div className="flex gap-2 overflow-x-auto max-w-full mb-8 pb-1 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
          {featurePills.map((pill) => (
            <span
              key={pill.label}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.07] border border-white/[0.12] text-white text-xs whitespace-nowrap"
            >
              <i className={`${pill.icon} text-gold`} />
              {pill.label}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 w-full max-w-[320px]">
          <Link
            to="/signup"
            className="w-full h-[52px] flex items-center justify-center rounded-2xl bg-gold text-midnight text-base font-semibold transition-transform hover:scale-[0.98] active:scale-95 animate-fade-up"
          >
            Get Started
          </Link>
          <Link
            to="/login"
            className="w-full h-[52px] flex items-center justify-center rounded-2xl bg-white/[0.07] border border-white/[0.15] text-white text-base font-medium transition hover:bg-white/[0.12]"
          >
            Sign In
          </Link>
          {billingEnabled && (
            <Link
              to="/pricing"
              className="text-sm text-white/45 hover:text-white/70 hover:underline transition text-center mt-1"
            >
              View pricing
            </Link>
          )}
        </div>

        {/* Powered by */}
        <a
          href="https://codelabsus.com"
          target="_blank"
          rel="noreferrer"
          className="mt-10 inline-flex items-center gap-1.5 text-[11px] text-white/30 hover:text-white/50 transition"
        >
          <i className="ri-terminal-box-line text-electric text-sm" />
          Powered by CodeLab
        </a>
      </div>
    </div>
  );
}